import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, Flex, Heading, Text, Skeleton, useMediaQuery, useToast, Button } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import AppAccountsTable from './AppAccountsTable.tsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.tsx';
import { UserRole } from '../../constants/UserRoles.ts';
import { ICareHome } from '../../interfaces';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';
import AleaFilterTags from '../../components/AleaFilterTags/AleaFilterTags.tsx';
import SearchBar from '../../components/SearchBar/SearchBar';
import AleaListFilter from '../../components/AleaListFilter/AleaListFilter.tsx';
import AleaDownloadButton, { DownloadOption } from '../../components/AleaDownloadButton/AleaDownloadButton';
import { AleaSelectedTags } from '../../components/AleaSelectedTag';
import CareHomeIdDisplay from '../../components/CareHomeIdDisplay/CareHomeIdDisplay';
import CaregiversInformation from '../../components/CaregiversInformation/CaregiversInformation';
import AddAppAccountModal from '../../modals/AddAppAccountModal/AddAppAccountModal';
import AleaLoader from '../../components/AleaLoader/AleaLoader';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal';
import AleaInfoModal from '../../modals/AleaInfoModal/AleaInfoModal';
import { debounce } from 'lodash';
import { A4ReportDocument, type ReportColumn, type ReportRow } from '../../pdf/reports/A4ReportDocument';
import formatLastLoginDate from '../../utils/formatLastLoginDate';
import { downloadReactPdf, formatExportTimestamp, printReactPdf } from '../../utils/reactPdfExport';
import { SeniSmartLogo } from '../../pdf/logos/SeniSmartLogo';
import { buildCsv, downloadCsv, type CsvColumn } from '../../utils/csvExport';
import { IAccount } from '../../interfaces/Account/IAccount.ts';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';
import {
  useActiveAppAccountsCountQuery,
  useAppAccountsListQuery,
  useCareHomeForAppAccountsQuery,
  useCareHomesForAppAccountsQuery,
} from './appAccountsPage.queries.ts';
import { useAppAccountsPageMutations } from './appAccountsPage.mutations.ts';

const AppAccountsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRestoring } = usePageFiltersStorage();
  const { user } = useAuth();
  const toast = useToast();
  const [isLargerThan1120] = useMediaQuery('(min-width: 1120px)');
  
  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();
  
  // Store current search input value that comes directly from SearchBar
  const inputValueRef = useRef<string>('');
  
  // Extract query from URL search params
  const queryParams = new URLSearchParams(location.search);
  const urlQueryParam = queryParams.get('query') || '';
  const urlPageParam = parseInt(queryParams.get('page') || '1', 10);
  const urlPerPageParam = queryParams.get('perPage');
  const showArchived = queryParams.get('show_archived') === 'true';

  // State for search query - initialize from URL
  const [searchQuery, setSearchQuery] = useState(urlQueryParam);

  // Table skeleton loader (e.g. immediate feedback while typing before debounce fires)
  const [isTableLoading, setIsTableLoading] = useState(false);

  // State for active row action menu (for global backdrop & focus)
  const [activeActionRowId, setActiveActionRowId] = useState<string | null>(null);

  // State for add/edit app account modal
  const [isAddAppAccountModalOpen, setIsAddAppAccountModalOpen] = useState(false);

  // State for editing caregiver in modal
  const [editingCaregiver, setEditingCaregiver] = useState<IAccount | null>(null);

  // State for archive confirmation modal
  const [caregiverToArchive, setCaregiverToArchive] = useState<IAccount | null>(null);

  // State for archiving info modal
  const [isArchivingInfoOpen, setIsArchivingInfoOpen] = useState(false);
  const [archivingCaregiverName, setArchivingCaregiverName] = useState<string | null>(null);

  // State for reactivating info modal
  const [isReactivatingInfoOpen, setIsReactivatingInfoOpen] = useState(false);
  const [reactivatingCaregiverName, setReactivatingCaregiverName] = useState<string | null>(null);

  // State for adding caregiver info modal
  const [isAddingCaregiverInfoOpen, setIsAddingCaregiverInfoOpen] = useState(false);
  // State for updating caregiver username info modal
  const [isUpdatingUserNameInfoOpen, setIsUpdatingUserNameInfoOpen] = useState(false);

  // State for information box
  const [isInformationOpen, setIsInformationOpen] = useState(false);

  // State for current page - initialize from URL
  const [currentPage, setCurrentPage] = useState(urlPageParam);
  
  // State for perPage - If URL has perPage, use it, otherwise use stored value
  const [perPage, setPerPage] = useState(urlPerPageParam ? parseInt(urlPerPageParam, 10) : storedPerPage);
  
  // State for filter tag (Active/Archived) - initialize from URL
  const [selectedFilter, setSelectedFilter] = useState(showArchived ? 'archived' : 'active');

  const [isExporting, setIsExporting] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // CRUD actions are handled as React Query mutations (extracted to a separate file)
  const {
    refreshCareHomeRegistrationIdMutation,
    updateCaregiverActiveStatusMutation,
    invalidateAppAccounts,
    invalidateActiveCount,
    isCrudLoading,
  } = useAppAccountsPageMutations();

  useEffect(() => {
    if (isRestoring) return;

    const params = new URLSearchParams(location.search);
    let shouldUpdate = false;

    // Keep URL canonical so React Query queryKey stays stable
    if (!params.get('page')) {
      params.set('page', '1');
      shouldUpdate = true;
    }
    if (!params.get('perPage')) {
      params.set('perPage', storedPerPage.toString());
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [isRestoring, location.pathname, location.search, navigate, storedPerPage]);

  // Update states when URL changes
  useEffect(() => {
    const queryFromUrl = queryParams.get('query') || '';
    const pageFromUrl = parseInt(queryParams.get('page') || '1', 10);
    const perPageFromUrlRaw = queryParams.get('perPage');
    const perPageFromUrl = perPageFromUrlRaw ? parseInt(perPageFromUrlRaw, 10) : storedPerPage;
    const isArchived = queryParams.get('show_archived') === 'true';
    
    if (searchQuery !== queryFromUrl) {
      setSearchQuery(queryFromUrl);
    }
    
    if (currentPage !== pageFromUrl) {
      setCurrentPage(pageFromUrl);
    }

    if (perPage !== perPageFromUrl) {
      setPerPage(perPageFromUrl);
    }

    if (selectedFilter !== (isArchived ? 'archived' : 'active')) {
      setSelectedFilter(isArchived ? 'archived' : 'active');
    }
  }, [location.search, perPage, storedPerPage]);

  // Handle search input changes - this is called immediately when user types
  const handleSearch = (inputValue: string) => {
    // Store the latest input value in ref for debounce function to access
    inputValueRef.current = inputValue;

    // Show table skeleton loader immediately when user types (if value changed)
    if (inputValue !== searchQuery) {
      setIsTableLoading(true);
    }

    debouncedUpdateUrl();
  };

  // Debounced function to update URL with the latest input value
  const debouncedUpdateUrl = useCallback(
    debounce(() => {
      // Use the current value from ref to ensure we have the latest input
      const latestInput = inputValueRef.current;
      const newSearchParams = new URLSearchParams(location.search);
      
      if (latestInput) {
        newSearchParams.set('query', latestInput);
      } else {
        newSearchParams.delete('query');
      }
      
      // Reset to page 1 when searching
      newSearchParams.set('page', '1');
      
      // Update the URL - this will trigger the fetchCaregivers via useEffect
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    }, 1000),
    [location.pathname, location.search, navigate]
  );

  // Clear search handler
  const handleClearSearch = () => {
    // Clear the ref value
    inputValueRef.current = '';
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('query');
    // Reset to page 1 when clearing search
    newSearchParams.set('page', '1');
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  const carehomeIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('carehome_id') || undefined;
  }, [location.search]);

  const careHomesQuery = useCareHomesForAppAccountsQuery(
    user?.organisation?.id,
    user?.role === UserRole.ORGANISATION_MANAGER
  );

  const careHomes = useMemo((): ICareHome[] => {
    if (user?.role === UserRole.ORGANISATION_MANAGER) {
      return careHomesQuery.data?.items ?? [];
    }

    if (!user?.carehome) return [];

    // For non-organisation managers, include only their own care home
    return [
      {
        id: user.carehome.id,
        name: user.carehome.name,
        organisation_id: user?.organisation?.id || '',
        address: {} as any,
        manager: {} as any,
        timezone: '',
        features: [],
        is_active: true,
        created_at: '',
        updated_at: '',
        deleted_at: null,
        registration_id: '',
        day_start: null,
        day_end: null,
        night_start: null,
        night_end: null,
        carehome_phone_data: { code: 0, number: 0 },
      },
    ];
  }, [careHomesQuery.data?.items, user?.carehome, user?.organisation?.id, user?.role]);

  const firstCareHomeId = careHomes.length > 0 ? careHomes[0].id : undefined;

  const targetCareHomeId = useMemo((): string | undefined => {
    if (user?.role === UserRole.ORGANISATION_MANAGER) {
      return carehomeIdFromUrl ?? firstCareHomeId;
    }
    return user?.carehome?.id || undefined;
  }, [carehomeIdFromUrl, firstCareHomeId, user?.carehome?.id, user?.role]);

  const careHomeQuery = useCareHomeForAppAccountsQuery(targetCareHomeId);
  const currentCareHomeData = careHomeQuery.data ?? null;

  const isQueryReady = useMemo(() => {
    if (isRestoring) return false;
    if (!queryParams.has('perPage')) return false;
    if (!queryParams.has('page')) return false;
    return true;
  }, [isRestoring, location.search]);

  const appAccountsQueryInput = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const page = parseInt(params.get('page') || '1', 10);
    const perPage = parseInt(params.get('perPage') || storedPerPage.toString(), 10);
    const query = params.get('query') || undefined;
    const isArchived = params.get('show_archived') === 'true';
    const orderBy = params.get('orderBy') || undefined;
    const sortOrderRaw = params.get('sortOrder');
    const sortOrder =
      sortOrderRaw === 'asc' || sortOrderRaw === 'desc'
        ? (sortOrderRaw as 'asc' | 'desc')
        : undefined;

    const isOrganisationManager = user?.role === UserRole.ORGANISATION_MANAGER;
    const organisationId = isOrganisationManager ? user?.organisation?.id : undefined;
    const carehomeId = isOrganisationManager ? params.get('carehome_id') || undefined : user?.carehome?.id;

    return {
      page,
      perPage,
      query,
      organisationId,
      carehomeId,
      isActive: !isArchived,
      orderBy,
      sortOrder,
    };
  }, [location.search, storedPerPage, user?.carehome?.id, user?.organisation?.id, user?.role]);

  const canRunAppAccountsQuery = useMemo(() => {
    if (!isQueryReady) return false;
    if (user?.role === UserRole.ORGANISATION_MANAGER) return !!user?.organisation?.id;
    return !!user?.carehome?.id;
  }, [isQueryReady, user?.carehome?.id, user?.organisation?.id, user?.role]);

  const appAccountsQuery = useAppAccountsListQuery(appAccountsQueryInput, canRunAppAccountsQuery);

  const activeCountQuery = useActiveAppAccountsCountQuery(
    { organisationId: appAccountsQueryInput.organisationId, carehomeId: appAccountsQueryInput.carehomeId },
    canRunAppAccountsQuery
  );

  useEffect(() => {
    if (!appAccountsQuery.isFetching) {
      setIsTableLoading(false);
    }
    // Also close skeleton on cache hits (queryKey changes without fetching)
  }, [appAccountsQuery.isFetching, appAccountsQuery.dataUpdatedAt, appAccountsQuery.errorUpdatedAt]);

  // Make sure to clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateUrl.cancel();
    };
  }, [debouncedUpdateUrl]);

  const handlePageChange = (page: number) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', page.toString());
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };
  
  // Handler for perPage changes
  const handlePerPageChange = (newPerPage: number) => {
    updatePerPage(newPerPage); // Update localStorage
    setPerPage(newPerPage);
    setCurrentPage(1);
    
    const newParams = new URLSearchParams(location.search);
    newParams.set('perPage', newPerPage.toString());
    newParams.set('page', '1');
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Filter tags for Active and Archived
  const filterTags = [
    { label: t('active'), value: 'active' },
    { label: t('general.archived'), value: 'archived' },
  ];

  // Handle filter tag selection
  const handleFilterSelect = (value: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    
    if (value === 'archived') {
      newSearchParams.set('show_archived', 'true');
    } else {
      // Keep explicit value so filter restore does not keep stale 'true' from storage
      newSearchParams.set('show_archived', 'false');
    }
    
    newSearchParams.set('page', '1'); // Reset to page 1 when changing filters
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  const buildPdfDocument = (exportDate: Date) => {
    const showCareHomeColumnForPdf = user?.role !== UserRole.CAREHOME_MANAGER;

    const columns: ReportColumn[] = [
      { key: 'username', header: t('username'), flex: 2, align: 'left' },
      ...(showCareHomeColumnForPdf
        ? ([{ key: 'carehome', header: t('careHome'), flex: 2, align: 'left' }] as ReportColumn[])
        : []),
      { key: 'last_login', header: t('lastLoggedIn'), flex: 1, align: 'left' },
    ];

    const getCareHomeName = (carehomeId: string | null | undefined): string => {
      if (!carehomeId) return '-';
      const careHome = careHomes.find((ch) => ch.id === carehomeId);
      return careHome?.name || '-';
    };

    const rows: ReportRow[] = appAccounts.map((acc) => ({
      username: acc.username || '-',
      ...(showCareHomeColumnForPdf ? { carehome: getCareHomeName(acc.carehome_id) } : {}),
      last_login: formatLastLoginDate(acc.last_login),
    }));

    const carehomeIdForFilter =
      user?.role === UserRole.ORGANISATION_MANAGER
        ? queryParams.get('carehome_id') || undefined
        : user?.carehome?.id;

    const carehomeNameForFilter = carehomeIdForFilter
      ? careHomes.find((ch) => ch.id === carehomeIdForFilter)?.name || currentCareHomeData?.name || '-'
      : currentCareHomeData?.name || '-';

    return (
      <A4ReportDocument
        title={t('appAccounts')}
        subtitle={t('careGiverCount', { totalCount, onlineCount })}
        headerRightLeadingLogo={{ src: '/assets/images/logos/alea-cockpit-logo.png', height: 27 }}
        headerRightLabel={t('exclusively_with')}
        headerRightTrailing={<SeniSmartLogo width={73} height={24} />}
        filterLabel={selectedFilter === 'archived' ? t('general.archived') : t('active')}
        filterTagVariant={selectedFilter === 'archived' ? 'archived' : 'active'}
        filterValue={carehomeNameForFilter}
        columns={columns}
        rows={rows}
        organisationName={user?.organisation?.name}
        exportedAtLabel={t('general.export', { defaultValue: 'Export' })}
        exportedAtValue={formatExportTimestamp(exportDate)}
        pageLabel={t('general.page', { defaultValue: 'Page' })}
      />
    );
  };

  // Print using the same PDF layout as "Export as PDF"
  const handlePrint = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportDate = new Date();
      await printReactPdf({
        title: `app-accounts-${exportDate.toISOString().slice(0, 10)}`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (e) {
      console.error('Failed to print PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle export as PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    
    try {
      const exportDate = new Date();
      const exportDay = exportDate.toISOString().slice(0, 10);
      setIsExporting(true);

      await downloadReactPdf({
        filename: `app-accounts-report-${exportDay}.pdf`,
        reactPdfDocument: buildPdfDocument(exportDate),
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCsv = () => {
    const exportDate = new Date();
    const exportDay = exportDate.toISOString().slice(0, 10);

    const showCareHomeColumnForExport = user?.role !== UserRole.CAREHOME_MANAGER;

    const getCareHomeName = (carehomeId: string | null | undefined): string => {
      if (!carehomeId) return '-';
      const careHome = careHomes.find((ch) => ch.id === carehomeId);
      return careHome?.name || '-';
    };

    type CsvRow = IAccount;
    const columns: CsvColumn<CsvRow>[] = [
      { header: t('username'), value: (r) => r.username || '-' },
      ...(showCareHomeColumnForExport
        ? ([{ header: t('careHome'), value: (r) => getCareHomeName(r.carehome_id) }] as CsvColumn<CsvRow>[])
        : []),
      { header: t('lastLoggedIn'), value: (r) => formatLastLoginDate(r.last_login) },
    ];

    const rows = appAccounts;
    const csv = buildCsv(columns, rows);
    downloadCsv(`app-accounts-report-${exportDay}.csv`, csv);
  };

  // Dynamic download options
  const downloadOptions: DownloadOption[] = [
    {
      id: 'print',
      label: 'Print',
      translationKey: 'general.print',
      onClick: handlePrint
    },
    {
      id: 'pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: handleExportPdf
    },
    {
      id: 'csv',
      label: 'Export as CSV',
      translationKey: 'general.exportAsCsv',
      onClick: handleExportCsv,
    }
  ];

  // Generate filters for AleaListFilter component (using rstate key for simple list mode)
  // Also include carehome_id for AleaSelectedTags to work correctly
  const filters = {
    rstate: user?.role === UserRole.ORGANISATION_MANAGER ? careHomes.map((careHome) => ({
      label: careHome.name,
      value: careHome.name,
      id: careHome.id,
    })) : [],
    carehome_id: user?.role === UserRole.ORGANISATION_MANAGER ? careHomes.map((careHome) => ({
      label: careHome.name,
      value: careHome.name,
      id: careHome.id,
    })) : [],
  };

  const appAccounts = appAccountsQuery.data?.items ?? [];
  const totalCount = appAccountsQuery.data?.total || 0;
  const onlineCount = activeCountQuery.data ?? 0;
  const isSearching = !!queryParams.get('query');
  const isLoading =
    !isExporting &&
    (isCrudLoading ||
      appAccountsQuery.isLoading ||
      (!isSearching && appAccountsQuery.isFetching) ||
      activeCountQuery.isLoading ||
      (!isSearching && activeCountQuery.isFetching));

  // Handle refresh care home ID
  const handleRefreshCareHomeId = async () => {
    if (!currentCareHomeData?.id) return;

    try {
      // Generate new ID in format LL99-AA99-A9
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const digits = '0123456789';

      const getRandomLetter = () => letters.charAt(Math.floor(Math.random() * letters.length));
      const getRandomDigit = () => digits.charAt(Math.floor(Math.random() * digits.length));

      // Get prefix from care home name
      const name = currentCareHomeData.name || '';
      const words = name.trim().split(/\s+/);
      let prefix: string;
      if (words.length >= 2) {
        // Multi-word: first letter of first two words
        prefix = (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      } else {
        // Single word: first two characters
        prefix = name.substring(0, 2).toUpperCase();
      }

      const part1 = prefix + getRandomDigit() + getRandomDigit(); // LL99
      const part2 = getRandomLetter() + getRandomLetter() + getRandomDigit() + getRandomDigit(); // AA99

      const newId = `${part1}-${part2}`;
      
      await refreshCareHomeRegistrationIdMutation.mutateAsync({
        careHomeId: currentCareHomeData.id,
        registrationId: newId,
      });
      
      // Show success toast
      toast({
        title: t('success'),
        description: t('careHome.registrationIdUpdatedSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to refresh care home ID', error);
      toast({
        title: t('error'),
        description: t('careHome.registrationIdUpdateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleArchiveCaregiver = async (caregiver: IAccount) => {
    try {
      await updateCaregiverActiveStatusMutation.mutateAsync({
        caregiver,
        isActive: false,
      });
    } catch (error) {
      console.error('Failed to archive caregiver', error);
      toast({
        title: t('error'),
        description: t('caregiver.archiveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleReactivateCaregiver = async (caregiver: IAccount) => {
    try {
      setReactivatingCaregiverName(caregiver.username);
      setIsReactivatingInfoOpen(true);

      await updateCaregiverActiveStatusMutation.mutateAsync({
        caregiver,
        isActive: true,
      });
    } catch (error) {
      console.error('Failed to reactivate caregiver', error);
      toast({
        title: t('error'),
        description: t('caregiver.reactivateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsReactivatingInfoOpen(false);
      setReactivatingCaregiverName(null);
    }
  };

  const handleEditCaregiver = (caregiver: IAccount) => {
    setEditingCaregiver(caregiver);
    setIsAddAppAccountModalOpen(true);
  };

  const handleRequestArchiveCaregiver = (caregiver: IAccount) => {
    setActiveActionRowId(null);
    setCaregiverToArchive(caregiver);
  };

  return (
    <Box data-testid="app-accounts-page">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        mb={{ base: '20px', md: '10px' }}
        h={{ base: 'auto', md: 75 }}
        flexDirection="row"
      >
        <Flex flexDirection="column">
          <Heading
            size="lg"
            fontSize={25}
            fontWeight={500}
            pl={{ base: 0, md: 0 }}
            mb={{ base: 1, md: 0 }}
          >
            {t('appAccounts')}
          </Heading>
          {isLoading ? (
            <Skeleton height="24px" width="200px" mt="4px" startColor="#c0c0c0" endColor="#e0e0e0" />
          ) : (
            <Text fontSize={15} fontWeight={300} color="alea.500" mt="4px">
              {t('careGiverCount', { totalCount, onlineCount })}
            </Text>
          )}
        </Flex>
        {currentCareHomeData && user?.role !== UserRole.ORGANISATION_MANAGER && (
          <CareHomeIdDisplay
            careHomeId={currentCareHomeData.registration_id || ''}
            onRefresh={handleRefreshCareHomeId}
          />
        )}
      </Flex>

      {/* Information Component and Add Caregiver Button (hidden for organisation managers) */}
      {user?.role !== UserRole.ORGANISATION_MANAGER && (
        isInformationOpen ? (
          // When information is open: full width info box, button below
          <Box mb="16px">
            <CaregiversInformation 
              isOpen={isInformationOpen} 
              onToggle={setIsInformationOpen}
            />
            <Flex justifyContent="flex-end" mt="16px">
              <Button
                data-testid="app-accounts-add-button"
                leftIcon={<AddIcon />}
                colorScheme="teal"
                variant="solid"
                h="50px"
                borderRadius="20px"
                fontWeight={400}
                onClick={() => {
                  setEditingCaregiver(null);
                  setIsAddAppAccountModalOpen(true);
                }}
                fontSize="20px"
                whiteSpace="nowrap"
              >
                {t('addAppAccount')}
              </Button>
            </Flex>
          </Box>
        ) : (
          // When information is closed: button and add caregiver in same row
          <Flex justifyContent="space-between" alignItems="center" mb="16px" gap="16px" flexWrap="wrap">
            <Box>
              <CaregiversInformation 
                isOpen={isInformationOpen} 
                onToggle={setIsInformationOpen}
              />
            </Box>
            <Box flexShrink={0}>
              <Button
                data-testid="app-accounts-add-button"
                leftIcon={<AddIcon />}
                colorScheme="teal"
                variant="solid"
                h="50px"
                borderRadius="20px"
                fontWeight={400}
                onClick={() => {
                  setEditingCaregiver(null);
                  setIsAddAppAccountModalOpen(true);
                }}
                fontSize="20px"
                whiteSpace="nowrap"
              >
                {t('addAppAccount')}
              </Button>
            </Box>
          </Flex>
        )
      )}

      <Box mb="25px">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={{ base: 'column', md: isLargerThan1120 ? 'row' : 'column' }}
          gap={{ base: '16px' }}
          flexWrap={'wrap'}
        >
          <AleaFilterTags
            tags={filterTags}
            selectedTags={[selectedFilter]}
            onTagSelect={handleFilterSelect}
          />
          <Flex 
            gap="10px" 
            alignItems="center" 
            position="relative"
            alignSelf="flex-start"
            width={{ base: '100%', md: 'auto' }}
          >
            <Box w={{ base: '100%', md: '320px' }}>
              <SearchBar
                onClose={handleClearSearch}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isHardwarePage={true}
                height="50px"
                placeholderLanguageKey="userNameCaregiver"
              />
            </Box>
            {user?.role === UserRole.ORGANISATION_MANAGER && (
              <AleaListFilter
                filters={filters}
                isSimpleList={true}
                dropdownWidth="450px"
              />
            )}
            <AleaDownloadButton options={downloadOptions} />
          </Flex>
        </Flex>
      </Box>

      {/* Selected filter tags */}
      <AleaSelectedTags filters={filters} />

      <Box 
        bg="white" 
        borderRadius="20px" 
        overflow="auto" 
        minHeight={500}
        ref={tableContainerRef}
        className="caregivers-table-container"
      >
        <AppAccountsTable
          appAccounts={appAccounts}
          isLoading={isLoading || isTableLoading}
          careHomes={careHomes}
          total={totalCount}
          usePagination={true}
          currentPage={currentPage}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onEditCaregiver={handleEditCaregiver}
          onArchiveCaregiver={handleRequestArchiveCaregiver}
          onReactivateCaregiver={handleReactivateCaregiver}
          isArchivedView={selectedFilter === 'archived'}
          canManageCaregivers={user?.role !== UserRole.ORGANISATION_MANAGER}
          showCareHomeColumn={user?.role !== UserRole.CAREHOME_MANAGER}
          activeActionRowId={activeActionRowId}
          setActiveActionRowId={setActiveActionRowId}
        />
      </Box>

      {refreshCareHomeRegistrationIdMutation.isPending && <AleaLoader />}

      {/* Add App Account Modal */}
      <AddAppAccountModal
        isOpen={isAddAppAccountModalOpen}
        onClose={() => setIsAddAppAccountModalOpen(false)}
        onConfirm={() => {
          if (editingCaregiver) {
            setIsUpdatingUserNameInfoOpen(true);
            setTimeout(() => {
              setIsUpdatingUserNameInfoOpen(false);
            }, 2000);
          } else {
            setIsAddingCaregiverInfoOpen(true);
            setTimeout(() => {
              setIsAddingCaregiverInfoOpen(false);
            }, 2000);
          }
          // Refresh app accounts list after adding/updating
          invalidateAppAccounts();
          invalidateActiveCount();
        }}
        carehomeId={
          user?.role === UserRole.ORGANISATION_MANAGER
            ? queryParams.get('carehome_id') || undefined
            : user?.carehome?.id
        }
        caregiver={editingCaregiver}
      />

      {/* Archive Caregiver Confirmation Modal */}
      <AleaConfirmModal
        isOpen={!!caregiverToArchive}
        onClose={() => setCaregiverToArchive(null)}
        title={
          caregiverToArchive
            ? t('archiveCaregiverConfirmationHeadline', {
                caregiverName: caregiverToArchive.username,
              })
            : ''
        }
        description={t('archiveAppAccountConfirmationSubline')}
        titleColor="sunsetEmber.500"
        onConfirm={async () => {
          if (!caregiverToArchive) return;
          const username = caregiverToArchive.username;
          const caregiver = caregiverToArchive;
          setCaregiverToArchive(null);
          setArchivingCaregiverName(username);
          setIsArchivingInfoOpen(true);
          await handleArchiveCaregiver(caregiver);
          setIsArchivingInfoOpen(false);
          setArchivingCaregiverName(null);
        }}
      />

      {/* Archiving Caregiver Info Modal */}
      <AleaInfoModal
        isOpen={isArchivingInfoOpen}
        onClose={() => {
          setIsArchivingInfoOpen(false);
          setArchivingCaregiverName(null);
        }}
        message={
          archivingCaregiverName
            ? t('archivingCaregiver', { caregiverName: archivingCaregiverName })
            : t('archivingCaregiver', { caregiverName: '' })
        }
      />

      {/* Adding App Account Info Modal */}
      <AleaInfoModal
        isOpen={isAddingCaregiverInfoOpen}
        onClose={() => setIsAddingCaregiverInfoOpen(false)}
        message={t('addingAppAccount')}
      />

      {/* Updating Caregiver Username Info Modal */}
      <AleaInfoModal
        isOpen={isUpdatingUserNameInfoOpen}
        onClose={() => setIsUpdatingUserNameInfoOpen(false)}
        message={t('updatingUserName')}
      />

      {/* Reactivating Caregiver Info Modal */}
      <AleaInfoModal
        isOpen={isReactivatingInfoOpen}
        onClose={() => {
          setIsReactivatingInfoOpen(false);
          setReactivatingCaregiverName(null);
        }}
        message={
          reactivatingCaregiverName
            ? t('reactivatingAppAccount', { appAccount: reactivatingCaregiverName })
            : t('reactivatingAppAccount', { appAccount: '' })
        }
      />

      {/* Backdrop when caregiver row action menu is open */}
      {activeActionRowId && (
        <Box
          position="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.20)"
          zIndex={1500}
          onClick={() => setActiveActionRowId(null)}
        />
      )}
    </Box>
  );
};

export default AppAccountsPage;
